var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : new P(function (resolve) { resolve(result.value); }).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
var __generator = (this && this.__generator) || function (thisArg, body) {
    var _ = { label: 0, sent: function() { if (t[0] & 1) throw t[1]; return t[1]; }, trys: [], ops: [] }, f, y, t, g;
    return g = { next: verb(0), "throw": verb(1), "return": verb(2) }, typeof Symbol === "function" && (g[Symbol.iterator] = function() { return this; }), g;
    function verb(n) { return function (v) { return step([n, v]); }; }
    function step(op) {
        if (f) throw new TypeError("Generator is already executing.");
        while (_) try {
            if (f = 1, y && (t = op[0] & 2 ? y["return"] : op[0] ? y["throw"] || ((t = y["return"]) && t.call(y), 0) : y.next) && !(t = t.call(y, op[1])).done) return t;
            if (y = 0, t) op = [op[0] & 2, t.value];
            switch (op[0]) {
                case 0: case 1: t = op; break;
                case 4: _.label++; return { value: op[1], done: false };
                case 5: _.label++; y = op[1]; op = [0]; continue;
                case 7: op = _.ops.pop(); _.trys.pop(); continue;
                default:
                    if (!(t = _.trys, t = t.length > 0 && t[t.length - 1]) && (op[0] === 6 || op[0] === 2)) { _ = 0; continue; }
                    if (op[0] === 3 && (!t || (op[1] > t[0] && op[1] < t[3]))) { _.label = op[1]; break; }
                    if (op[0] === 6 && _.label < t[1]) { _.label = t[1]; t = op; break; }
                    if (t && _.label < t[2]) { _.label = t[2]; _.ops.push(op); break; }
                    if (t[2]) _.ops.pop();
                    _.trys.pop(); continue;
            }
            op = body.call(thisArg, _);
        } catch (e) { op = [6, e]; y = 0; } finally { f = t = 0; }
        if (op[0] & 5) throw op[1]; return { value: op[0] ? op[1] : void 0, done: true };
    }
};
import { differenceBy, unionBy } from 'lodash';
import { LogFactory } from '../modules/config/logging';
import { castMediaError } from '../modules/RTC/RTCMediaErrors';
export var Logger = LogFactory('Devices Utils');
/**
 * Properly clones a MediaDeviceInfo object, as it cannot be cloned normally in Edge.
 */
export function cloneMediaDeviceInfo(_a) {
    var deviceId = _a.deviceId, groupId = _a.groupId, kind = _a.kind, label = _a.label;
    return {
        deviceId: deviceId,
        groupId: groupId,
        kind: kind,
        label: label
    };
}
export function mergeByDeviceUID(a, b) {
    return unionBy(a, b, deviceUID);
}
export function differenceByDeviceUID(a, b) {
    return differenceBy(a, b, deviceUID);
}
export function areDeviceListsEqual(a, b) {
    // We determine that two MediaDeviceInfo are equal if the device ID, kind, and label are the same.
    // Group IDs change across sessions, so they're not useful.
    if (a && b && a.length === b.length) {
        var bSet_1 = b.reduce(function (value, device) {
            value.add(deviceUID(device) + "-" + device.label);
            return value;
        }, new Set());
        return a.every(function (device) { return bSet_1.has(deviceUID(device) + "-" + device.label); });
    }
    else {
        return a === b;
    }
}
export function removeUnwantedDevices(devices) {
    // TODO Cross-browser device filtering:
    // - In Chrome: filter deviceIds default and communications
    // - In Firefox: filter out the first audio input as it's the default
    // - In Edge: Don't filter anything
    return devices.filter(function (element) {
        return element.deviceId.toLowerCase() !== 'communications' &&
            element.deviceId.toLowerCase() !== 'default' &&
            // Firefox Quantum generates this odd device that can break getUserMedia
            element.label.indexOf('CubebAggregateDevice') !== 0;
    });
}
/**
 * Retrieve device being used as audio input
 */
export function getSelectedInput(devices, label) {
    return devices.filter(function (device) { return device.label === label && device.kind === 'audioinput'; })[0];
}
/**
 * Retrieve device being used as audio input
 */
export function getInputDevices(devices) {
    return devices.filter(function (element) { return element.kind === 'audioinput'; });
}
/**
 * Retrieve device being used as audio output
 */
export function getOutputDevices(devices) {
    return devices.filter(function (element) { return element.kind === 'audiooutput'; });
}
/**
 * Retrieve device being used as video input
 */
export function getVideoInputDevices(devices) {
    return devices.filter(function (element) { return element.kind === 'videoinput'; });
}
/**
 * Labels the list of devices, giving them a label according to the following
 * (in order of importance):
 * - The label of the new device.
 * - The label of the previously known device
 * - A generic default label. (e.g.: microphone 2)
 */
export function filterDevices(allDevices, newDevices) {
    var inputCount = 1;
    var outputCount = 1;
    var cameraCount = 1; // videoinput do not have default
    return newDevices.map(function (device) {
        var foundDevice = allDevices.find(function (currentDevice) {
            return deviceUID(device) === deviceUID(currentDevice) &&
                !/^(microphone|camera|speaker)\s\d+$/g.test(currentDevice.label);
        });
        var newDevice = cloneMediaDeviceInfo(device);
        if (foundDevice) {
            if (device.label === '') {
                newDevice.label = foundDevice.label;
            }
            else {
                newDevice.label = device.label;
            }
        }
        switch (device.kind) {
            case 'audioinput':
                newDevice.label = newDevice.label || 'microphone ' + inputCount;
                inputCount++;
                break;
            case 'audiooutput':
                newDevice.label = newDevice.label || 'speaker ' + outputCount;
                outputCount++;
                break;
            case 'videoinput':
                newDevice.label = newDevice.label || 'camera ' + cameraCount;
                cameraCount++;
                break;
        }
        return newDevice;
    });
}
/**
 * Update history with new prefered device id, we make sure that the history is never of greater size than 10.
 *
 * @param {string[]} history current history
 * @param {string} uId new prefered device
 * @returns {string[]} history with the added device
 */
export function updateDeviceHistory(history, uId) {
    var newHistory = history.filter(function (id) { return id !== uId; });
    if (newHistory.length >= 10) {
        newHistory = newHistory.slice(Math.max(0, newHistory.length - 9), newHistory.length);
    }
    newHistory.push(uId);
    return newHistory;
}
/**
 * Find if one of selected device is in the available devices
 * starting from last selected device
 */
export function getNextDevice(history, devices) {
    // faster with nested loops, instead of Array.find
    for (var i = history.length - 1; i >= 0; i--) {
        for (var j = 0; j < devices.length; j++) {
            if (history[i] === deviceUID(devices[j])) {
                return devices[j];
            }
        }
    }
    return devices[0];
}
export function tryMedia(mediaStreamConstraints) {
    if (mediaStreamConstraints === void 0) { mediaStreamConstraints = { audio: true, video: false }; }
    return __awaiter(this, void 0, void 0, function () {
        var stream, error_1;
        return __generator(this, function (_a) {
            switch (_a.label) {
                case 0:
                    _a.trys.push([0, 2, , 3]);
                    Logger.log('TryMedia constraints', mediaStreamConstraints);
                    return [4 /*yield*/, navigator.mediaDevices.getUserMedia(mediaStreamConstraints)];
                case 1:
                    stream = _a.sent();
                    Logger.log('TryMedia success', stream.getTracks());
                    return [2 /*return*/, stream];
                case 2:
                    error_1 = _a.sent();
                    Logger.error('TryMedia error', error_1);
                    throw castMediaError(error_1);
                case 3: return [2 /*return*/];
            }
        });
    });
}
export function tryDisplayMedia(mediaStreamConstraints) {
    if (mediaStreamConstraints === void 0) { mediaStreamConstraints = { audio: false, video: true }; }
    return __awaiter(this, void 0, void 0, function () {
        var stream, error_2;
        return __generator(this, function (_a) {
            switch (_a.label) {
                case 0:
                    _a.trys.push([0, 7, , 8]);
                    Logger.log('TryDisplayMedia constraints', mediaStreamConstraints);
                    stream = void 0;
                    if (!navigator.mediaDevices.getDisplayMedia) return [3 /*break*/, 2];
                    return [4 /*yield*/, navigator.mediaDevices.getDisplayMedia(mediaStreamConstraints)];
                case 1:
                    // Spec-compliant
                    stream = _a.sent();
                    return [3 /*break*/, 6];
                case 2:
                    if (!navigator.getDisplayMedia) return [3 /*break*/, 4];
                    return [4 /*yield*/, navigator.getDisplayMedia(mediaStreamConstraints)];
                case 3:
                    // Old-draft compliant
                    stream = _a.sent();
                    return [3 /*break*/, 6];
                case 4: return [4 /*yield*/, navigator.mediaDevices.getUserMedia({
                        video: {
                            mediaSource: 'screen'
                        } // For Firefox pre spec compliance
                    })];
                case 5:
                    stream = _a.sent();
                    _a.label = 6;
                case 6:
                    Logger.log('TryDisplayMedia success', stream.getTracks());
                    return [2 /*return*/, stream];
                case 7:
                    error_2 = _a.sent();
                    Logger.error('TryDisplayMedia error', error_2);
                    throw castMediaError(error_2);
                case 8: return [2 /*return*/];
            }
        });
    });
}
export function releaseStream(stream) {
    Logger.log('Release media stream', stream);
    stream.getTracks().forEach(function (track) { return track.stop(); });
}
/**
 * As of chrome 81 you now need to ask for permissions before requesting devices list
 * @link https://bugs.chromium.org/p/chromium/issues/detail?id=1019176
 *
 * The idea here is to allow the consumer to import this function and call for these permissions at
 * the right time for that app. Since the device manager polls for devices, the consumer can request the permissions
 * when the time is right and the polling will pick the devices up as they become available.
 *
 * Allowing for video request to be optional, this is to allow apps to make the decision
 * as to whether to request media permissions based on a users entitlements
 *
 */
export function getDevicesPermissions(includeVideoConstraints, includeAudioConstraints) {
    if (includeVideoConstraints === void 0) { includeVideoConstraints = false; }
    if (includeAudioConstraints === void 0) { includeAudioConstraints = true; }
    return __awaiter(this, void 0, void 0, function () {
        var devices, audio, audioConstraints, video, videoConstraints, mediaConstraints, stream;
        return __generator(this, function (_a) {
            switch (_a.label) {
                case 0: return [4 /*yield*/, getDevices()];
                case 1:
                    devices = _a.sent();
                    audio = devices.some(function (device) { return device.kind === 'audioinput'; });
                    audioConstraints = includeAudioConstraints ? { audio: audio } : {};
                    video = devices.some(function (device) { return device.kind === 'videoinput'; });
                    videoConstraints = includeVideoConstraints ? { video: video } : {};
                    mediaConstraints = __assign({}, audioConstraints, videoConstraints);
                    return [4 /*yield*/, navigator.mediaDevices.getUserMedia(mediaConstraints)];
                case 2:
                    stream = _a.sent();
                    releaseStream(stream);
                    return [2 /*return*/];
            }
        });
    });
}
export function getDevices() {
    return __awaiter(this, void 0, void 0, function () {
        var error_3, deviceError;
        return __generator(this, function (_a) {
            switch (_a.label) {
                case 0:
                    _a.trys.push([0, 3, , 4]);
                    if (!navigator.mediaDevices) return [3 /*break*/, 2];
                    return [4 /*yield*/, navigator.mediaDevices.enumerateDevices()];
                case 1: return [2 /*return*/, _a.sent()];
                case 2: return [2 /*return*/, []];
                case 3:
                    error_3 = _a.sent();
                    Logger.error('Get devices failed', error_3);
                    deviceError = {
                        errorCode: 'DEVICES_ERROR',
                        message: error_3.message || 'Get devices failed'
                    };
                    throw deviceError;
                case 4: return [2 /*return*/];
            }
        });
    });
}
export function deviceUID(device) {
    return device.deviceId + "-" + device.kind;
}
export function devicesEqual(device1, device2) {
    var uid1 = device1 ? deviceUID(device1) : null;
    var uid2 = device2 ? deviceUID(device2) : null;
    return uid1 === uid2;
}
export function buildAudioConstraints(devices) {
    var mediaStreamConstraints = {
        audio: true
    };
    if (devices) {
        mediaStreamConstraints.audio = devices.selectedInputDevice
            ? {
                deviceId: { exact: devices.selectedInputDevice.deviceId }
            }
            : mediaStreamConstraints.audio;
    }
    return mediaStreamConstraints;
}
//# sourceMappingURL=devices.utils.js.map